import React, { useState } from 'react'
import {
  IoClose,
  IoBookmarkOutline,
  IoFolderOutline,
  IoAddOutline,
  IoCheckmark,
} from 'react-icons/io5'
import {
  useCardsWatchlists,
  saveCardsFixture,
  createCardsWatchlist,
  addFixtureToWatchlist,
} from '../../../swr-config'
import type { WatchlistOut } from '../../../types'
import toast from 'react-hot-toast'

interface SaveToWatchlistModalProps {
  fixtureId: number
  fixtureName: string // e.g., "Arsenal vs Chelsea"
  onClose: () => void
  onSaved: () => void
  isAlreadySaved?: boolean
}

export function SaveToWatchlistModal({
  fixtureId,
  fixtureName,
  onClose,
  onSaved,
  isAlreadySaved = false,
}: SaveToWatchlistModalProps) {
  const [selectedWatchlistId, setSelectedWatchlistId] = useState<number | null>(null)
  const [notes, setNotes] = useState('')
  const [showCreateWatchlist, setShowCreateWatchlist] = useState(false)
  const [newWatchlistName, setNewWatchlistName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isCreatingWatchlist, setIsCreatingWatchlist] = useState(false)

  const { data: watchlistsData, mutate: mutateWatchlists } = useCardsWatchlists()

  const handleSave = async () => {
    if (isSaving) return

    setIsSaving(true)
    try {
      if (isAlreadySaved && selectedWatchlistId) {
        // Already saved, just add to watchlist
        await addFixtureToWatchlist(selectedWatchlistId, fixtureId, notes || undefined)
        toast.success('Added to watchlist')
      } else {
        // Save fixture (optionally to watchlist)
        await saveCardsFixture(fixtureId, selectedWatchlistId || undefined, notes || undefined)
        toast.success(selectedWatchlistId ? 'Saved to watchlist' : 'Fixture saved')
      }
      onSaved()
      onClose()
    } catch (err: any) {
      toast.error(err.message || 'Failed to save fixture')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreateWatchlist = async () => {
    if (!newWatchlistName.trim() || isCreatingWatchlist) return

    setIsCreatingWatchlist(true)
    try {
      const created = await createCardsWatchlist(newWatchlistName.trim())
      toast.success('Watchlist created')
      setNewWatchlistName('')
      setShowCreateWatchlist(false)
      setSelectedWatchlistId(created.id)
      mutateWatchlists()
    } catch (err: any) {
      toast.error(err.message || 'Failed to create watchlist')
    } finally {
      setIsCreatingWatchlist(false)
    }
  }

  const handleWatchlistSelect = (watchlist: WatchlistOut) => {
    setSelectedWatchlistId(selectedWatchlistId === watchlist.id ? null : watchlist.id)
  }

  // Handle escape key to close
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-modal-title"
    >
      <div className="save-watchlist-modal">
        {/* Header */}
        <div className="save-modal-header">
          <div className="save-modal-header-content">
            <IoBookmarkOutline size={20} />
            <h2 id="save-modal-title">
              {isAlreadySaved ? 'Add to Watchlist' : 'Save Fixture'}
            </h2>
          </div>
          <button
            type="button"
            className="save-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IoClose size={20} />
          </button>
        </div>

        {/* Fixture Info */}
        <div className="save-modal-fixture">
          <span className="save-modal-fixture-name">{fixtureName}</span>
        </div>

        {/* Notes Input */}
        <div className="save-modal-notes">
          <label htmlFor="save-notes" className="save-modal-label">
            Notes (optional)
          </label>
          <textarea
            id="save-notes"
            className="save-modal-textarea"
            placeholder="Add a note about this fixture..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={500}
          />
        </div>

        {/* Watchlist Selection */}
        <div className="save-modal-watchlists">
          <div className="save-modal-watchlists-header">
            <label className="save-modal-label">Add to Watchlist (optional)</label>
            <button
              type="button"
              className="save-modal-new-watchlist-btn"
              onClick={() => setShowCreateWatchlist(!showCreateWatchlist)}
            >
              <IoAddOutline size={16} />
              <span>New</span>
            </button>
          </div>

          {/* Create Watchlist Form */}
          {showCreateWatchlist && (
            <div className="save-modal-create-watchlist">
              <input
                type="text"
                className="save-modal-input"
                placeholder="Watchlist name..."
                value={newWatchlistName}
                onChange={(e) => setNewWatchlistName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateWatchlist()}
                autoFocus
              />
              <button
                type="button"
                className="save-modal-create-btn"
                onClick={handleCreateWatchlist}
                disabled={!newWatchlistName.trim() || isCreatingWatchlist}
              >
                {isCreatingWatchlist ? 'Creating...' : 'Create'}
              </button>
            </div>
          )}

          {/* Watchlist List */}
          {watchlistsData?.watchlists && watchlistsData.watchlists.length > 0 ? (
            <div className="save-modal-watchlist-list">
              {watchlistsData.watchlists.map((watchlist) => (
                <button
                  key={watchlist.id}
                  type="button"
                  className={`save-modal-watchlist-item ${
                    selectedWatchlistId === watchlist.id ? 'selected' : ''
                  }`}
                  onClick={() => handleWatchlistSelect(watchlist)}
                >
                  <IoFolderOutline size={16} />
                  <span className="save-modal-watchlist-name">{watchlist.name}</span>
                  <span className="save-modal-watchlist-count">
                    {watchlist.fixture_count} fixtures
                  </span>
                  {selectedWatchlistId === watchlist.id && (
                    <IoCheckmark size={16} className="save-modal-watchlist-check" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <p className="save-modal-no-watchlists">
              No watchlists yet. Create one to organize your saved fixtures.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="save-modal-actions">
          <button
            type="button"
            className="save-modal-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="save-modal-save-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isAlreadySaved ? 'Add to Watchlist' : 'Save Fixture'}
          </button>
        </div>
      </div>
    </div>
  )
}
